import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    Input,
    Optional,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEmpty as _isEmpty, isString as _isString, get as _get, isObject as _isObject } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

export interface AutocompleteOption {
    id: string;
    /** Displayed value */
    label?: string;
    /** Actual value */
    value?: any;
}

@Component({
    selector: 'ce-search-with-autocomplete',
    templateUrl: './search-with-autocomplete.component.html',
    styleUrls: ['./search-with-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class SearchWithAutocompleteComponent implements OnInit {
    @ViewChild('inputField') inputField: ElementRef;
    /** Options array to check against */
    @Input() @Optional() options: AutocompleteOption[] = [];
    /** Options observable to check against */
    @Input() @Optional() options$: Observable<AutocompleteOption[]>;
    /** When using defaultCheck, if option's value is an object, use values from these properties for filtering */
    @Input() @Optional() checkValueBasedOn: string[] | string;
    /** Filter results based on this function. Fall back to default check if not provided */
    @Input() @Optional() customCheck: (term: string, checkAgainst: any) => boolean;
    @Input() @Optional() placeholder = '';
    /** When set to true, displays placeholder text as a label instead of placeholder inside the input field */
    @Input() @Optional() showLabel = false;
    /** When set to true, displays dropdown icon as a suffix of the input field */
    @Input() @Optional() showDropdownIcon = false;
    /** Initial option (should be taken from the options array). Value also updated when using initialOption$ */
    @Input() @Optional() initialOption: AutocompleteOption;
    /** Initial option (should be taken from the options$ observable) */
    @Input() @Optional() initialOption$: Observable<AutocompleteOption>;
    /** When set to true, focuses input field on load */
    @Input() @Optional() focusInputOnLoad = false;
    /** Displays loading indicator on the right side of input field */
    @Input() @Optional() loading = false;
    /** Emitter, fire when an option is selected */
    @Output() optionSelected: EventEmitter<AutocompleteOption> = new EventEmitter();
    /** Emitter, fire when an option is confirmed (enter) */
    @Output() optionConfirmed: EventEmitter<AutocompleteOption> = new EventEmitter();

    formControl = new UntypedFormControl();
    private searchTerm$: Observable<any>;
    results$: Observable<AutocompleteOption[]>;

    /** Currently selected option. Should not be modified directly */
    selectedOption: AutocompleteOption;
    autocompleteOpen = false;

    ngOnInit(): void {
        // Input
        this.searchTerm$ = this.formControl.valueChanges.pipe(startWith(''), debounceTime(200), distinctUntilChanged());

        // Check initial option
        if (!_isEmpty(this.initialOption)) {
            this.formControl.setValue(this.initialOption);
            this.selectedOption = this.initialOption; // set selected option as well
        } else if (this.initialOption$) {
            this.initialOption$
                .pipe(
                    filter((val) => !_isEmpty(val)),
                    untilDestroyed(this)
                )
                .subscribe((val) => {
                    this.formControl.setValue(val);
                    this.initialOption = val;
                    this.selectedOption = val; // set selected option as well
                });
        }

        this.results$ = combineLatest([
            this.searchTerm$,
            this.options$ ? this.options$.pipe(startWith([])) : of([]),
        ]).pipe(
            map(([searchTerm, observableOptions]) => {
                const _options: AutocompleteOption[] = observableOptions.concat(this.options); // add options array to perform search if provided
                const results: AutocompleteOption[] = [];

                const defaultCheck = (term: string, checkAgainst: any, requiredLength = 0) => {
                    const _term = _isString(term) ? term.toLocaleLowerCase().trim() : '';
                    const _checkAgainst = _isString(checkAgainst) ? checkAgainst.toLocaleLowerCase().trim() : '';

                    if (_term.length > requiredLength) {
                        return _checkAgainst.includes(_term);
                    }
                    return false;
                };
                const isUnique = (option: AutocompleteOption, source: AutocompleteOption[]): boolean => {
                    return !source.some((existed) => option.id === existed.id);
                };

                // Return all if empty text field
                if (!searchTerm || !searchTerm?.length) {
                    return _options;
                }

                // Check
                for (const option of _options) {
                    if (this.customCheck) {
                        // Custom check
                        if (this.customCheck(searchTerm, option)) {
                            if (isUnique(option, results)) {
                                results.push(option);
                                continue;
                            }
                        }
                    } else {
                        // Fall back to default check

                        // label / displayed value
                        if (defaultCheck(searchTerm, option?.label)) {
                            if (isUnique(option, results)) {
                                results.push(option);
                                continue;
                            }
                        }

                        // id
                        if (defaultCheck(searchTerm, option.id)) {
                            if (isUnique(option, results)) {
                                results.push(option);
                                continue;
                            }
                        }

                        // option's value
                        const _value = option?.value;
                        if (_isString(_value)) {
                            if (defaultCheck(searchTerm, option.value)) {
                                if (isUnique(option, results)) {
                                    results.push(option);
                                    continue;
                                }
                            }
                        } else if (_isObject(_value)) {
                            const _criteria: string[] = [];

                            if (_isString(this.checkValueBasedOn)) {
                                _criteria.push(this.checkValueBasedOn);
                            }
                            if (this.checkValueBasedOn?.length) {
                                _criteria.push(...this.checkValueBasedOn);
                            }
                            // Looping through criteria
                            for (const c of _criteria) {
                                const _checkAgainst = _get(_value, c, undefined)?.toString() || _value[c]?.toString(); // _value[c] allows keys such as "just.for.fun"

                                if (defaultCheck(searchTerm, _checkAgainst)) {
                                    if (isUnique(option, results)) {
                                        results.push(option);
                                        break; // break loop immediately if match found
                                    }
                                }
                            }

                            // Continue with next option if option is pushed to results
                            if (!isUnique(option, results)) {
                                continue;
                            }
                        }
                    }
                }

                return results;
            }),
            untilDestroyed(this)
        );
    }

    ngAfterViewInit(): void {
        // Set focus on the input field after the view has been initialized
        if (this.focusInputOnLoad) {
            this.inputField.nativeElement.focus();
        }
    }
    confirmOption() {
        // emit confirmation when option is not anymore open and enter is pressed
        if (!_isEmpty(this.selectedOption) && !this.autocompleteOpen) {
            this.optionConfirmed.next(this.selectedOption);
        }
    }
    onAutocompleteOpened() {
        this.autocompleteOpen = true;
    }

    onSelect(event?: MatAutocompleteSelectedEvent) {
        const option = event?.option?.value;
        if (!_isEmpty(option)) {
            this.selectedOption = option;
            this.optionSelected.next(option);
            // timer to wait for the option to be selected
            setTimeout(() => {
                this.autocompleteOpen = false;
            }, 200);
        }
    }
    onInputFocusOut() {
        // Reset to previously selected option if there is one
        if (!_isEmpty(this.selectedOption)) {
            this.formControl.setValue(this.selectedOption);
        } else {
            // Else, fallback to initial option
            this.formControl.setValue(this.initialOption);
            this.selectedOption = this.initialOption; // set selected option as well
        }
    }
    displayFn(option: AutocompleteOption): string {
        return option ? (option?.label ? option.label : option.id) : '';
    }
}
